import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser, SignInButton } from "@clerk/clerk-react";
import { Authenticated, Unauthenticated } from "convex/react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import {
  ArrowRight,
  Check,
  Shield,
  Users,
  Calendar,
  DollarSign,
  Menu,
  X,
  Sparkles,
  BrainCircuit,
  FileText,
  Zap,
  Calculator,
  TrendingUp,
  UserPlus,
  Clock,
  Star,
  Target,
  FileCheck,
  UserCog,
  Building2,
  MapPin,
  Package,
  LayoutDashboard,
  MessageCircle,
  BarChart3,
  BookOpen,
  LifeBuoy,
  ShieldCheck,
  ShieldAlert,
  Workflow,
  FileSignature,
  PieChart,
  Magnet,
  Play,
} from "lucide-react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

import { LandingNavbar } from "@/components/landing-navbar";
import { FAQChatWidget } from "@/components/support/FAQChatWidget";

// --- Assets ---
const ASSETS = {
  hero: "/assets/hero_dashboard_prism.png",
  core: "/assets/intelligent_core.png",
  demo: "/assets/document_demo.png",
  field: "/assets/field_ops.png",
  security: "/assets/security_shield.png",
  commandCenter: "/assets/command_center.png",
};

// --- Components ---

const HeroSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroTextRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!heroTextRef.current || !dashboardRef.current || !containerRef.current) return;

      const tl = gsap.timeline();

      tl.from(heroTextRef.current.children, {
        y: 50,
        opacity: 0,
        duration: 1,
        stagger: 0.2,
        ease: "power3.out",
      });

      tl.from(
        dashboardRef.current,
        {
          y: 100,
          opacity: 0,
          duration: 1.2,
          ease: "power3.out",
        },
        "-=0.5"
      );

      // Subtle parallax for background
      gsap.to(bgRef.current, {
        y: 30,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    },
    { scope: containerRef }
  );

  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <section
      ref={containerRef}
      className="relative flex min-h-[90vh] flex-col justify-start overflow-hidden bg-white pb-24 pt-16 md:pb-32"
    >
      {/* Full Background Image */}
      <div
        ref={bgRef}
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 -mb-[10%] h-[120%] w-full"
      >
        <img
          src="/assets/hero_dashboard_prism.png"
          alt="ManageClean OS Automation Hub"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/40 to-transparent" />
      </div>

      <div className="container relative z-10 mx-auto mt-4 flex flex-col items-center px-6 md:mt-8">
        {/* Soft white radial glow to ensure text readability without looking like a rigid box */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[150%] w-[150vw] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.95)_0%,rgba(255,255,255,0.8)_30%,rgba(255,255,255,0)_70%)] blur-xl md:w-[120%]" />

        <div ref={heroTextRef} className="relative mx-auto max-w-4xl px-2 text-center">
          <h1 className="mb-6 text-3xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl md:text-7xl">
            Build a cleaning company{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              that runs without you
            </span>
          </h1>

          <p className="mx-auto mb-6 max-w-4xl px-2 text-base font-medium leading-relaxed text-slate-600 sm:text-lg md:text-xl">
            Stop managing every detail and start leading from the boardroom. ManageClean OS deploys
            smart protocols through the Automation Hub to handle the chaos of operations, staff, and
            growth—giving you the freedom to scale instead of just survive.
          </p>

          <div className="flex w-full flex-col items-center justify-center gap-4 sm:w-auto sm:flex-row">
            <Unauthenticated>
              <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
                <Button
                  size="lg"
                  className="h-12 w-full rounded-full bg-slate-900 px-8 text-lg text-white hover:bg-slate-800 sm:w-auto"
                >
                  Start Transformation <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </SignInButton>
            </Unauthenticated>
            <Authenticated>
              <Link to="/dashboard">
                <Button
                  size="lg"
                  className="h-12 w-full rounded-full bg-blue-600 px-8 text-lg text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700 sm:w-auto"
                >
                  Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Authenticated>

            <Button
              variant="outline"
              size="lg"
              onClick={() => setIsVideoOpen(true)}
              className="h-12 w-full rounded-full border-slate-200 bg-white/50 px-8 text-lg text-slate-600 backdrop-blur-sm hover:bg-slate-50 sm:w-auto flex items-center justify-center gap-2"
            >
              <Play className="h-4 w-4 fill-slate-600 text-slate-600" />
              Watch Demo
            </Button>

            <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
              <DialogContent className="max-w-4xl border-slate-800 bg-slate-950 p-0 text-white shadow-2xl overflow-hidden sm:rounded-2xl">
                <DialogHeader className="p-4 pb-0">
                  <DialogTitle className="text-lg font-medium text-slate-200">
                    ManageClean OS Platform Demo
                  </DialogTitle>
                </DialogHeader>
                <div className="relative aspect-video w-full p-2 pt-0">
                  {isVideoOpen && (
                    <iframe
                      className="h-full w-full rounded-xl border border-slate-800"
                      src="https://www.youtube.com/embed/g8lZClxoeGk?autoplay=1&rel=0"
                      title="ManageClean OS Demo Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Dashboard UI Mockup */}
        <div ref={dashboardRef} className="relative z-10 mx-auto mt-16 w-full max-w-6xl">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200/50 bg-white/50 shadow-[0_0_60px_-15px_rgba(0,0,0,0.3)] backdrop-blur-sm">
            {/* The user will drop their attached image here */}
            <img
              src="/assets/dashboard_hero.png"
              alt="ManageClean OS Interface"
              className="block h-auto w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const IntelligentCore = () => {
  return (
    <section
      id="core"
      className="relative overflow-hidden bg-slate-950 py-24 text-white"
    >
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />

      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center gap-16 lg:flex-row">
          <div className="relative lg:w-1/2">
            <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 shadow-[0_0_50px_rgba(59,130,246,0.2)]">
              <img
                src="/assets/intelligent_core.png"
                alt="AI Intelligent Core"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            </div>
            {/* Glowing connection lines simulation */}
            <div className="absolute left-1/2 top-1/2 -z-10 h-[150%] w-[150%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/20 blur-[100px]" />
          </div>

          <div className="space-y-8 lg:w-1/2">
            <div className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider text-blue-400">
              <span className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />
              Build 1.0 • Intelligent Core
            </div>

            <h2 className="text-4xl font-bold leading-tight md:text-5xl">
              An Agentic AI that <br /> <span className="text-blue-400">runs your operations.</span>
            </h2>

            <p className="text-lg leading-relaxed text-slate-400">
              More than just a chatbot. The ManageClean OS agent acts autonomously—resolving
              anomalies, drafting client communications, and orchestrating your business data in
              real time.
            </p>

            <div className="grid grid-cols-1 gap-6 pt-4 sm:grid-cols-2">
              <div className="group flex cursor-default gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-colors hover:bg-white/10">
                <div className="flex-shrink-0 rounded-lg bg-blue-500/20 p-3 text-blue-400 transition-transform group-hover:scale-110">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="mb-1 font-semibold text-white">Autonomous Issue Resolution</h3>
                  <p className="text-sm text-slate-400">
                    Detects operational anomalies like missed shifts or supply shortages, and
                    proactively resolves them.
                  </p>
                </div>
              </div>

              <div className="group flex cursor-default gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-colors hover:bg-white/10">
                <div className="flex-shrink-0 rounded-lg bg-purple-500/20 p-3 text-purple-400 transition-transform group-hover:scale-110">
                  <Workflow className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="mb-1 font-semibold text-white">Native Tool Execution</h3>
                  <p className="text-sm text-slate-400">
                    Drafts Google Docs, creates slide decks, and manages your emails natively on
                    your behalf.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturePillars = () => {
  const agenticFeatures = [
    {
      title: "Intelligent Business Assistant",
      desc: "Acts as a second brain for your operations, answering queries and surfacing real-time insights instantly.",
      icon: BrainCircuit,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      glowColor: "bg-blue-500/5",
      glowHover: "group-hover:bg-blue-500/10",
      borderHover: "hover:border-blue-200",
    },
    {
      title: "AI Document Writer",
      desc: "Automatically drafts professional proposals, scope of work documents, and contracts tailored to the cleaning industry.",
      icon: FileText,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-600",
      glowColor: "bg-indigo-500/5",
      glowHover: "group-hover:bg-indigo-500/10",
      borderHover: "hover:border-indigo-200",
    },
    {
      title: "Automated Task",
      desc: "Deploy pre-configured system tasks and micro-automations that act as your operational safety net. From automated lead follow-ups to status-change triggers and weather alerts, your AI Assistant handles background tasks seamlessly.",
      icon: Workflow,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
      glowColor: "bg-amber-500/5",
      glowHover: "group-hover:bg-amber-500/10",
      borderHover: "hover:border-amber-200",
    },
    {
      title: "Smart Bid Workloading",
      desc: "Leverages industry standards and AI to automatically calculate required hours and labor costs for new facilities.",
      icon: Calculator,
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
      glowColor: "bg-emerald-500/5",
      glowHover: "group-hover:bg-emerald-500/10",
      borderHover: "hover:border-emerald-200",
    },
    {
      title: "Dynamic Profit Analysis",
      desc: "Calculates real-time margins and warns you before submitting an underpriced bid, ensuring profitability.",
      icon: TrendingUp,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
      glowColor: "bg-green-500/5",
      glowHover: "group-hover:bg-green-500/10",
      borderHover: "hover:border-green-200",
    },
    {
      title: "Automated Client Onboarding",
      desc: "AI-driven checklists and document flows that seamlessly transition a won lead into a fully operational client.",
      icon: UserPlus,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
      glowColor: "bg-purple-500/5",
      glowHover: "group-hover:bg-purple-500/10",
      borderHover: "hover:border-purple-200",
    },
    {
      title: "Interactive Bid Dashboard",
      desc: "Monitor your win rates, track outstanding proposals, and manage your entire bidding pipeline in one view.",
      icon: PieChart,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
      glowColor: "bg-orange-500/5",
      glowHover: "group-hover:bg-orange-500/10",
      borderHover: "hover:border-orange-200",
    },
    {
      title: "Automated QA & Review Loops",
      desc: "Automatically sends review requests and immediately flags negative feedback for rapid resolution.",
      icon: Star,
      iconBg: "bg-yellow-50",
      iconColor: "text-yellow-600",
      glowColor: "bg-yellow-500/5",
      glowHover: "group-hover:bg-yellow-500/10",
      borderHover: "hover:border-yellow-200",
    },
  ];

  const operationalFeatures = [
    {
      title: "Centralized CRM",
      desc: "Track every prospect, conversation, and follow-up in one place to scale your sales engine.",
      icon: Target,
      iconBg: "bg-red-50",
      iconColor: "text-red-600",
    },
    {
      title: "Professional Proposals",
      desc: "Generate comprehensive digital proposals detailing scope of work, service schedules, and transparent pricing.",
      icon: FileCheck,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      title: "Comprehensive HR",
      desc: "Manage staff profiles, roles, permissions, and compliance tracking effortlessly.",
      icon: UserCog,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-600",
    },
    {
      title: "Subcontractor Management",
      desc: "Manage subcontractor profiles, track compliance documents, and seamlessly assign them to service locations.",
      icon: Building2,
      iconBg: "bg-teal-50",
      iconColor: "text-teal-600",
    },
    {
      title: "Lead Pipeline Management",
      desc: "Capture, nurture, and convert commercial cleaning leads with automated follow-ups and intelligent scoring.",
      icon: Magnet,
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
    },
    {
      title: "Asset & Inventory",
      desc: "Keep tabs on equipment locations, maintenance logs, and chemical supply levels across all sites.",
      icon: Package,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      title: "Operations Dashboards",
      desc: "Clear, actionable dashboards that give you a bird's-eye view of daily operations and outstanding tasks.",
      icon: LayoutDashboard,
      iconBg: "bg-sky-50",
      iconColor: "text-sky-600",
    },
    {
      title: "Built-in Team Chat",
      desc: "Connect the front office to the field with instant, organized messaging and announcements.",
      icon: MessageCircle,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
    },
    {
      title: "Financial Analytics",
      desc: "Deep insights into revenue trends, labor costs, and overall business health to drive scaling decisions.",
      icon: BarChart3,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      title: "Knowledge Base",
      desc: "Centralize your standard operating procedures (SOPs) for consistent training and flawless execution.",
      icon: BookOpen,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
    },
    {
      title: "Automated Support",
      desc: "Resolve client and employee issues faster with a structured, trackable support ticketing system.",
      icon: LifeBuoy,
      iconBg: "bg-rose-50",
      iconColor: "text-rose-600",
    },
    {
      title: "Enterprise Security",
      desc: "Keep your sensitive client data, pricing models, and employee records safe with top-tier encryption.",
      icon: ShieldCheck,
      iconBg: "bg-slate-100",
      iconColor: "text-slate-700",
    },
  ];

  return (
    <section id="features" className="bg-slate-50 py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-20 max-w-4xl text-center">
          <h2 className="mb-6 text-3xl font-bold text-slate-900 md:text-5xl">
            Complete Operational Control
          </h2>
          <p className="text-balance text-lg text-slate-600">
            From the front office to the field, ManageClean OS covers every aspect of your
            janitorial business. Let our AI handle the chaos of operations so you can focus on
            leading your company.
          </p>
        </div>

        {/* Agentic Features Section */}
        <div className="mb-24">
          <div className="mb-12 flex flex-col items-center text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 font-medium text-blue-700">
              <Sparkles className="h-4 w-4" />
              Agentic & Automation Engine
            </div>
            <h3 className="max-w-2xl text-balance text-2xl font-bold text-slate-900 md:text-3xl">
              Build a company that runs without you
            </h3>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {agenticFeatures.map((f, i) => (
              <motion.div
                key={`agentic-${i}`}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 ${f.borderHover}`}
              >
                <div
                  className={`absolute right-0 top-0 h-32 w-32 ${f.glowColor} -mr-10 -mt-10 rounded-full blur-2xl ${f.glowHover} transition-colors duration-500`}
                />
                <div
                  className={`h-12 w-12 rounded-xl ${f.iconBg} flex items-center justify-center ${f.iconColor} relative z-10 mb-5`}
                >
                  <f.icon className="h-6 w-6" />
                </div>
                <h4 className="relative z-10 mb-2 text-lg font-bold text-slate-900">{f.title}</h4>
                <p className="relative z-10 text-sm leading-relaxed text-slate-600">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Operational Control Section */}
        <div>
          <div className="mb-12 flex flex-col items-center text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 font-medium text-blue-700">
              <LayoutDashboard className="h-4 w-4" />
              Core Operational Platform
            </div>
            <h3 className="max-w-2xl text-balance text-2xl font-bold text-slate-900 md:text-3xl">
              Remove the chaos and focus on scaling
            </h3>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {operationalFeatures.map((f, i) => (
              <motion.div
                key={`ops-${i}`}
                whileHover={{ y: -5 }}
                className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md"
              >
                <div
                  className={`h-10 w-10 rounded-lg ${f.iconBg} flex items-center justify-center ${f.iconColor} mb-4`}
                >
                  <f.icon className="h-5 w-5" />
                </div>
                <h4 className="mb-2 text-base font-bold text-slate-900">{f.title}</h4>
                <p className="text-sm leading-relaxed text-slate-500">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const DocumentWriterSection = () => {
  return (
    <section className="relative overflow-hidden border-y border-slate-200 bg-white py-24">
      {/* Abstract Background */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <img
          src="/assets/hero_dashboard_prism.png"
          alt="Abstract Background"
          className="h-full w-full -scale-x-100 object-cover object-center opacity-70"
        />

        {/* Left-side specific effects: Frosted blur and soft white gradient fading to transparent on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-white/0 backdrop-blur-[6px] [mask-image:linear-gradient(to_right,black_40%,transparent_80%)]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="flex flex-col items-center gap-16 lg:flex-row">
          <div className="lg:w-1/2">
            <h2 className="mb-6 text-3xl font-bold text-slate-900 md:text-4xl">
              Documents that <span className="text-blue-600">write themselves.</span>
            </h2>
            <p className="mb-8 text-lg text-slate-600">
              The AI Document Writer transforms your ideas into polished, professional documents.
              From scope of work proposals to contracts and invoices — generate industry-standard
              documents in seconds.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="rounded-lg bg-blue-100 p-2 text-blue-600">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="mb-1 font-semibold text-slate-900">AI-Powered Generation</h3>
                  <p className="text-sm text-slate-600">
                    Describe what you need in plain English. The AI understands janitorial
                    terminology and industry best practices.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="rounded-lg bg-green-100 p-2 text-green-600">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="mb-1 font-semibold text-slate-900">Professional Templates</h3>
                  <p className="text-sm text-slate-600">
                    Scope of work, proposals, contracts, invoices, and more. Save your best
                    documents as reusable templates.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="rounded-lg bg-purple-100 p-2 text-purple-600">
                  <ArrowRight className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="mb-1 font-semibold text-slate-900">One-Click Export</h3>
                  <p className="text-sm text-slate-600">
                    Download as PDF with your company branding. Send directly to clients or save to
                    your document library.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2">
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-2xl">
              <img
                src="/assets/document_demo.png"
                alt="Document Writer Interface"
                className="h-auto w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FieldOperations = () => {
  return (
    <section className="overflow-hidden bg-slate-900 py-24 text-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center gap-16 lg:flex-row-reverse">
          <div className="relative lg:w-1/2">
            <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-slate-950 shadow-[0_0_50px_rgba(59,130,246,0.15)]">
              <img
                src="/assets/mobile_agent_companion.png"
                alt="Agent-First Mobile App"
                className="h-auto w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-900/40 to-transparent" />
            </div>
            {/* Subtle glow behind the image */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-[80px]" />
          </div>

          <div className="space-y-8 lg:w-1/2">
            <div className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider text-amber-400">
              Deskless, not disconnected.
            </div>

            <h2 className="text-4xl font-bold leading-tight md:text-5xl">
              The Agent-First <br /> <span className="text-blue-400">Field Companion.</span>
              <span className="ml-3 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/20 px-2.5 py-0.5 align-middle text-xs font-medium text-amber-400">
                Beta
              </span>
            </h2>

            <p className="text-lg text-slate-400">
              Empower your frontline team with a chat-centric nervous system. Cleaners simply text
              the AI to clock in, report safety hazards with a photo, or request supplies—while
              managers get 1-tap approval cards at HQ.
            </p>

            <div className="grid grid-cols-1 gap-4 pt-2">
              <div className="flex items-start gap-3 text-slate-300">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                  <Check className="h-3 w-3" />
                </div>
                <div>
                  <strong className="mb-0.5 block text-white">Omni-Box Interface</strong>
                  <span className="text-sm text-slate-400">
                    Ditch complex menus. Action pills provide one-tap access to schedules and safety
                    docs.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 text-slate-300">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                  <Check className="h-3 w-3" />
                </div>
                <div>
                  <strong className="mb-0.5 block text-white">Contextual AI Vision</strong>
                  <span className="text-sm text-slate-400">
                    Snap a photo and let the agent automatically analyze physical site conditions.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 text-slate-300">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                  <Check className="h-3 w-3" />
                </div>
                <div>
                  <strong className="mb-0.5 block text-white">Smart Clock-In</strong>
                  <span className="text-sm text-slate-400">
                    GPS-verified tracking with automated anomaly alerts if shifts are uncovered.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 text-slate-300">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                  <Check className="h-3 w-3" />
                </div>
                <div>
                  <strong className="mb-0.5 block text-white">Human-in-the-Loop</strong>
                  <span className="text-sm text-slate-400">
                    Complex requests generate clean "Approve/Reject" cards directly to the manager.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CommandCenter = () => {
  return (
    <section className="overflow-hidden bg-slate-900 py-24 text-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center gap-16 lg:flex-row-reverse">
          <div className="relative lg:w-1/2">
            <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-slate-950 shadow-[0_0_50px_rgba(59,130,246,0.15)]">
              <img
                src={ASSETS.commandCenter}
                alt="Command Center Dashboard"
                className="h-auto w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-900/40 to-transparent" />
            </div>
            {/* Subtle glow behind the image */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-[80px]" />
          </div>

          <div className="space-y-8 lg:w-1/2">
            <div className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider text-amber-400">
              Total Operational Visibility.
            </div>

            <h2 className="text-4xl font-bold leading-tight md:text-5xl">
              The Agent-Powered <br /> <span className="text-blue-400">Command Center.</span>
            </h2>

            <p className="text-lg text-slate-400">
              Your entire cleaning operation centralized into one intelligent hub. The AI monitors real-time service status, automatically flags supply shortages, and drafts response plans for customer complaints—all before you even finish your morning coffee.
            </p>

            <div className="grid grid-cols-1 gap-4 pt-2">
              <div className="flex items-start gap-3 text-slate-300">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                  <Check className="h-3 w-3" />
                </div>
                <div>
                  <strong className="mb-0.5 block text-white">God-View Map</strong>
                  <span className="text-sm text-slate-400">
                    Live geospatial tracking of all active properties and on-site staff.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 text-slate-300">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                  <Check className="h-3 w-3" />
                </div>
                <div>
                  <strong className="mb-0.5 block text-white">Predictive Alerts</strong>
                  <span className="text-sm text-slate-400">
                    AI flags potential issues before they become client complaints.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 text-slate-300">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                  <Check className="h-3 w-3" />
                </div>
                <div>
                  <strong className="mb-0.5 block text-white">Automated Resolution</strong>
                  <span className="text-sm text-slate-400">
                    One-click approval for AI-drafted responses to supply requests or schedule shifts.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SecuritySection = () => {
  return (
    <section className="bg-blue-50 py-24">
      <div className="container mx-auto px-4 text-center">
        <div className="mb-8 flex justify-center">
          <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white p-4 shadow-lg">
            <img
              src="/assets/security_shield.png"
              alt="Security Shield"
              className="h-full w-full object-contain"
            />
          </div>
        </div>

        <h2 className="mb-4 text-3xl font-bold text-slate-900">Enterprise-Grade Security</h2>
        <p className="mx-auto mb-12 max-w-2xl text-slate-600">
          Built with modern security standards to keep your client data, bid pricing, and employee
          records safe.
        </p>

        <div className="flex flex-wrap justify-center gap-8 opacity-60 grayscale transition-all duration-500 hover:grayscale-0">
          {/* Mock Logos for Trust Signals */}
          <div className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <Shield className="h-5 w-5" /> SOC2 Compliant
          </div>
          <div className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <span className="rounded bg-slate-800 px-1 py-0.5 text-xs text-white">AES</span> 256-bit
            Encryption
          </div>
          <div className="flex items-center gap-2 text-xl font-bold text-slate-800">
            Enterprise Authentication
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <SEO
        title="Janitorial Business Management Software"
        description="Stop managing every detail and start leading from the boardroom. ManageClean OS deploys smart protocols through the Automation Hub to handle the chaos of operations, staff, and growth—giving you the freedom to scale instead of just survive."
        keywords={[
          "janitorial software",
          "cleaning business management",
          "commercial cleaning software",
          "janitorial CRM",
          "cleaning company software",
        ]}
        url="/"
        structuredData={[schemas.organization, schemas.webSite, schemas.softwareApplication]}
      />
      <LandingNavbar />
      <HeroSection />
      <IntelligentCore />
      <FeaturePillars />
      <DocumentWriterSection />
      {/* <FieldOperations /> */}
      <CommandCenter />
      <SecuritySection />

      <div className="border-t border-slate-900 bg-slate-950 py-12">
        <Footer />
      </div>

      {/* FAQ Chat Widget */}
      <FAQChatWidget />
    </div>
  );
}
